import _cloneDeep from 'lodash/cloneDeep';
import _first from 'lodash/first';
import _flatten from 'lodash/flatten';
import _last from 'lodash/last';
import _orderBy from 'lodash/orderBy';
import _remove from 'lodash/remove';
import _uniq from 'lodash/uniq';

class Question {
  constructor(data){
    this.id = data.id;
    this.type = data.type;
    this.phrase = data.phrase;
    this.sliderInterval = data.sliderInterval;
    this.tooltipTitle = data.tooltipTitle;
    this.tooltipDescription = data.tooltipDescription;
    this.answers = data.answers;
    this.position = data.position;
    this.disabled = true;
    // Now set disabled properties on answers.  We need to add this property
    // up-front - otherwise changes to it won't be watched reliably.
    // It's important (for filtering logic) that these starts disabled.
    if (this.answers) {
      for (let answer of this.answers) {
        answer.disabled = true;
      }
    }
  }


  debugLog(...args) {
    // console.log(args);
  }

  static all(data) {
    let questions = [];

    for (let question_data of data) {
      if (question_data.type == 'search') {
        questions.push(new SearchQuestion(question_data));
      } else if (question_data.type == 'checkboxes') {
        questions.push(new CheckboxesQuestion(question_data));
      } else if (question_data.type == 'dropdown') {
        questions.push(new DropdownQuestion(question_data));
      } else if (question_data.type == 'slider'){
        questions.push(new SliderQuestion(question_data));
      }
    }

    return questions;
  }

  static withAnswersForUrl(questions) {
    let qArr = [];
    for (let question of questions) {
      qArr.push(JSON.stringify(question.withAnswersForUrl()));
    }
    let newQs = qArr.join(',');
    let newState = `{ "questionsAnswered": [${newQs}]}`;

    return newState;
  }

  static questionsFilteredForProducts(allQuestions, products) {
    // this.debugLog('questionsFilteredForProducts');

    // This should return only the questions which have a matching answer for every single one of the products supplied
    // It should also set the disabled property for each question - so even if we override the list of which questions
    // to show - each question still knows whether it is disabled or not.

    let clone = Array.from(allQuestions);
    let remainingQuestions = _remove(clone, function(question) {
      return question.applicableForEveryProduct(products);
    });

    // Set the disabled property of each question - on all questions.
    let questionsToAskIds = remainingQuestions.map(x => x.id);
    for (let question of allQuestions) {
      if (questionsToAskIds.includes(question.id)) {
        question.disabled = false;
      } else {
        question.disabled = true;
      }
    }

    return remainingQuestions;
  }

  applicableForEveryProduct(products) {
    // this method could be in each specific class of question (and was for a while) if each is notably different
    // - i.e. if theres not just repetition of the same code

    if (this.type === 'search') { return true; } // always applicable/available/relevant

    if (!products.length) { return false; }

    // Need presence of an intersection between the question's answers and the product's answerIds
    // This must be for EVERY product!
    let hasAnswerForEveryProduct = true;

    let questionAnswerIds = this.answers.map(x => x.id);
    for (let product of products) {
      let intersection = product.answerIds.filter(x => questionAnswerIds.includes(x));
      if (!(intersection && intersection.length)) {
        hasAnswerForEveryProduct = false;
        break; // we may break as we're not building a list here now..
      }
    }

    return hasAnswerForEveryProduct; // i.e. - include this in the final remainingQuestions array
  }

  intersectingAnswerIds(products) {
    // Collate the intersecting answer id's
    let intersectingAnswerIds = []
    let questionAnswerIds = this.answers.map(x => x.id);
    for (let product of products) {
      let intersection = product.answerIds.filter(x => questionAnswerIds.includes(x));
      intersectingAnswerIds.push(intersection);
    }
    intersectingAnswerIds = _uniq(_flatten(intersectingAnswerIds));
    return intersectingAnswerIds;
  }

  disableAnswersIfNotIncluded(answers, intersectingAnswerIds) {
    for (let answer of answers) {
      if (!intersectingAnswerIds.includes(answer.id)) {
        answer.disabled = true;
      } else {
        answer.disabled = false;
      }
      // this.debugLog(answer.phrase, answer.disabled);
    }
  }

  disableFruitlessAnswers(products) {
    // this method could be in each specific class of question (and was for a while) if each is notably different
    // - i.e. if theres not just repetition of the same code

    if (this.type === 'search') { return; } // nothing to do
    if (!products.length) { return; }

    this.debugLog(`disableFruitlessAnswers for ${this.phrase}`, products);

    let intersectingAnswerIds = this.intersectingAnswerIds(products);
    // Disable answers from itself which aren't found on any product.
    // console.log(`About to set answers disabled flags set for ${this.phrase}`);
    this.disableAnswersIfNotIncluded(this.answers, intersectingAnswerIds);
  }

  showTooltip() {
    return (this.tooltipTitle || this.tooltipDescription);
  }
}



// These sub-classes are here as I haven't been able to work out how to import successfully from a separate file
// Perhaps as each file calls the other...?
class CheckboxesQuestion extends Question {
  constructor(data){
    super(data);
  }

  reset() {
    this.selectedAnswerIds = [];
  }

  setAnswer(answer) {
    this.selectedAnswerIds = answer;
  }

  withAnswersForUrl() {
    return {
      "questionId": this.id,
      "selectedAnswer": Object.assign([], this.selectedAnswerIds)
    }
  }

  sayMessage() {
    let message = '';

    if (this.selectedAnswerIds !== undefined) {
      for (let selectedAnswerId of this.selectedAnswerIds) {
        let answer = this.answers.find(x => x.id == selectedAnswerId);
        message += `${this.phrase}:  ${answer.phrase}`;
      }
    }

    return message;
  }
}

class DropdownQuestion extends Question {
  constructor(data){
    super(data);
  }

  reset() {
    this.selectedAnswerId = undefined;
  }

  setAnswer(answer) {
    this.selectedAnswerId = answer;
  }

  withAnswersForUrl() {
    return {
      "questionId": this.id,
      "selectedAnswer": this.selectedAnswerId
    }
  }

  sayMessage() {
    let message = '';

    if (this.selectedAnswerId !== undefined) {
      let selectedAnswer = this.answers.find(a => a.id == this.selectedAnswerId);
      if (selectedAnswer !== undefined) {
        message = `${this.phrase}:  ${selectedAnswer.phrase}  (id of ${selectedAnswer.id})`;
      }
    }

    return message;
  }
}

class SliderQuestion extends Question {
  constructor(data){
    super(data);
    // Note:  At this moment we havent loaded the products yet, so we can't yet set disabled properties/range/values
    // Also remember - sliders must always have some values to start with, so I'm relying on min and max being set later on, before rendering.
  }

  reset() {
    this.selectedAnswers = [this.min, this.max];
  }

  setAnswer(answer) {
    // this.debugLog(`Within question.setAnswer:  setting this.selectedAnswers for ${this.phrase} to be ${answer}`);
    this.selectedAnswers = answer;
  }

  setRange(products) {
    // This set of products will be different(larger) than the remaining pool
    //  - so we don't want to be affecting the data of which answers are disabled
    //  - so we deep clone this - and work against that.  This method will only
    // be called once on load - so the deep clone shouldn't end up bloating memory
    // Lodash was the only effective deep clone.  Array.from and Object.assign were both not deep enough
    this.debugLog(`Within setRange for ${this.phrase}, about to DEEP CLONE`);

    let intersectingAnswerIds = this.intersectingAnswerIds(products);
    let thisClone = _cloneDeep(this);
    this.disableAnswersIfNotIncluded(thisClone.answers, intersectingAnswerIds);

    let minMax = SliderQuestion.minMaxNonDisabledAnswers(thisClone.answers);
    if (!minMax) { return; }
    this.min = minMax[0];
    this.max = minMax[1];
    this.debugLog(`min: ${this.min}, max: ${this.max}`);
  }

  autoSetSelectedAnswers() {
    this.debugLog(`Auto-setting selected answers for ${this.phrase}`);
    let minMax = SliderQuestion.minMaxNonDisabledAnswers(this.answers);
    if (!minMax) {
      this.debugLog(`minMax is: ${minMax} (no answer options possible)`);
      // Probably not an issue.  Probably loading and disabled as has no valid
      // options to offer.  Just [re]set to its min and max
      if (this.selectedAnswers === undefined) { this.reset(); }
      return;
    }
    this.selectedAnswers = minMax;
    this.debugLog(`this.selectedAnswers: ${this.selectedAnswers}`);
  }

  static minMaxNonDisabledAnswers(answers) {
    let filteredAnswers = answers.filter(x => !x.disabled);
    let orderedAnswers = _orderBy(filteredAnswers, function (answer){
      return Number(answer.phrase);
    });
    if (orderedAnswers.length === 0) { return; }
    let answersMin = Number(_first(orderedAnswers).phrase);
    let answersMax = Number(_last(orderedAnswers).phrase);
    // console.log('minMaxNonDisabledAnswers', answersMin, answersMax);
    return [answersMin, answersMax];
  }

  withAnswersForUrl() {
    return {
      "questionId": this.id,
      "selectedAnswer": Object.assign([], this.selectedAnswers)
    }
  }

  sayMessage() {
    let message = '';

    message = `${this.phrase}:  ${this.selectedAnswers} ${Date.now()}`;

    return message;
  }
}

class SearchQuestion extends Question {
  constructor(data){
    super(data);
  }

  reset() {
    this.answer = '';
  }

  setAnswer(answer) {
    this.answer = answer;
  }

  withAnswersForUrl() {
    return {
      "questionId": this.id,
      "selectedAnswer": this.answer
    }
  }

  sayMessage() {
    let message = '';

    message = `${this.phrase}:  ${this.answer}`;

    return message;
  }
}

export default Question;
